/**
 * FO-432 (batch-59) - pure kern van de patroonherkenning voor interne
 * (intercompany) INKOOPfacturen. DB-loos en unit-getest; de API-laag levert de
 * rijen aan en schrijft het resultaat weg.
 *
 * Geen AI: dit is een aggregatie over al geboekte facturen. Een patroon is
 * "zelfde BV-paar + vergelijkbare omschrijving + vergelijkbaar bedrag + vast
 * ritme over >= N opeenvolgende periodes".
 *
 * GELD: alle bedragen reizen hier als HELE CENTEN (integer). De API converteert
 * `Prisma.Decimal` -> centen en terug; er wordt nooit met floats over geld
 * gerekend (mediaan en tolerantie zijn integer-operaties).
 *
 * PERIODES: sleutels zijn `YYYY-MM` (maand), `YYYY-Pnn` (4-wekelijks, verankerd
 * op ISO-weken zoals `facturatie-overzicht`) en `YYYY-Qn` (kwartaal). Alle
 * grenzen worden in Europe/Amsterdam bepaald en als UTC-instant teruggegeven.
 */

import { amsterdamAllDayStartToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'

/** Ritme-vocabulaire (spiegelt `InterneHerhaalRitme` in @streep/types). */
export type InternRitme = 'MAANDELIJKS' | 'VIER_WEKEN' | 'KWARTAAL'

const DAG_MS = 86_400_000

/** Hoeveel dagen één periode van dit ritme ongeveer beslaat (voor ritme-detectie). */
export const INTERN_RITME_DAGEN: Record<InternRitme, number> = {
  MAANDELIJKS: 30,
  VIER_WEKEN: 28,
  KWARTAAL: 91,
}

// ─── Periode-sleutels ────────────────────────────────────────────────────────

/** ISO-weeknummer + ISO-jaar van een NL-kalenderdag (ma-zo, week 1 = week met 4 jan). */
function isoWeekVanNlDag(jaar: number, maand: number, dag: number): { jaar: number; week: number } {
  // Rekenen op een UTC-datum die de NL-kalenderdag representeert: de ISO-week is
  // een kalenderbegrip, geen klok-begrip.
  const d = new Date(Date.UTC(jaar, maand - 1, dag))
  const dow = (d.getUTCDay() + 6) % 7 // ma = 0
  d.setUTCDate(d.getUTCDate() - dow + 3) // donderdag van deze week
  const isoJaar = d.getUTCFullYear()
  const eersteDonderdag = new Date(Date.UTC(isoJaar, 0, 4))
  const eersteDow = (eersteDonderdag.getUTCDay() + 6) % 7
  eersteDonderdag.setUTCDate(eersteDonderdag.getUTCDate() - eersteDow + 3)
  const week = 1 + Math.round((d.getTime() - eersteDonderdag.getTime()) / (7 * DAG_MS))
  return { jaar: isoJaar, week }
}

/** NL-kalenderdag (jaar/maand/dag) van een instant. */
function nlKalenderdag(d: Date): { jaar: number; maand: number; dag: number } {
  const { ymd } = utcToAmsterdamWallclock(d)
  return {
    jaar: Number(ymd.slice(0, 4)),
    maand: Number(ymd.slice(5, 7)),
    dag: Number(ymd.slice(8, 10)),
  }
}

/** Periode-sleutel van een instant, in Europe/Amsterdam. */
export function internePeriodeVanDatum(d: Date, ritme: InternRitme): string {
  const w = nlKalenderdag(d)
  if (ritme === 'MAANDELIJKS') {
    return `${w.jaar}-${String(w.maand).padStart(2, '0')}`
  }
  if (ritme === 'KWARTAAL') {
    return `${w.jaar}-Q${Math.floor((w.maand - 1) / 3) + 1}`
  }
  // VIER_WEKEN: blok-index op het 1-gebaseerde ISO-weeknummer (week 1-4 = P1).
  const iso = isoWeekVanNlDag(w.jaar, w.maand, w.dag)
  const blok = Math.floor((iso.week - 1) / 4) + 1
  return `${iso.jaar}-P${String(blok).padStart(2, '0')}`
}

export interface InternePeriode {
  jaar: number
  ritme: InternRitme
  /** 1-12 (maand), 1-4 (kwartaal), 1-14 (4-wekenblok). */
  index: number
}

/** Parse een periode-sleutel; null bij een onbekende vorm. */
export function parseInternePeriode(periode: string): InternePeriode | null {
  const maand = /^(\d{4})-(\d{2})$/.exec(periode)
  if (maand) {
    const index = Number(maand[2])
    if (index < 1 || index > 12) return null
    return { jaar: Number(maand[1]), ritme: 'MAANDELIJKS', index }
  }
  const kwartaal = /^(\d{4})-Q([1-4])$/.exec(periode)
  if (kwartaal) {
    return { jaar: Number(kwartaal[1]), ritme: 'KWARTAAL', index: Number(kwartaal[2]) }
  }
  const vierWeken = /^(\d{4})-P(\d{2})$/.exec(periode)
  if (vierWeken) {
    const index = Number(vierWeken[2])
    if (index < 1 || index > 14) return null
    return { jaar: Number(vierWeken[1]), ritme: 'VIER_WEKEN', index }
  }
  return null
}

const MAAND_NAMEN = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
]

/** Mens-leesbaar label van een periode-sleutel ("augustus 2026", "Q3 2026", "periode 8 2026"). */
export function internePeriodeLabel(periode: string): string {
  const p = parseInternePeriode(periode)
  if (!p) return periode
  if (p.ritme === 'MAANDELIJKS') return `${MAAND_NAMEN[p.index - 1]} ${p.jaar}`
  if (p.ritme === 'KWARTAAL') return `Q${p.index} ${p.jaar}`
  return `periode ${p.index} ${p.jaar}`
}

/** De eerstvolgende periode-sleutel na `periode`. */
export function interneVolgendePeriode(periode: string): string {
  const p = parseInternePeriode(periode)
  if (!p) return periode
  if (p.ritme === 'MAANDELIJKS') {
    return p.index === 12
      ? `${p.jaar + 1}-01`
      : `${p.jaar}-${String(p.index + 1).padStart(2, '0')}`
  }
  if (p.ritme === 'KWARTAAL') {
    return p.index === 4 ? `${p.jaar + 1}-Q1` : `${p.jaar}-Q${p.index + 1}`
  }
  // 4-weken: een ISO-jaar heeft 52 of 53 weken, dus 13 of 14 blokken. We rollen
  // pas naar het nieuwe jaar wanneer het blok buiten het weken-bereik valt; dat
  // bepaalt `internePeriodeStartUtc` exact.
  const volgend = p.index + 1
  const start = internePeriodeStartUtc(`${p.jaar}-P${String(volgend).padStart(2, '0')}`)
  const eersteVolgendJaar = internePeriodeStartUtc(`${p.jaar + 1}-P01`)
  if (start.getTime() >= eersteVolgendJaar.getTime()) return `${p.jaar + 1}-P01`
  return `${p.jaar}-P${String(volgend).padStart(2, '0')}`
}

/** UTC-instant van 00:00 NL op de eerste dag van de periode. */
export function internePeriodeStartUtc(periode: string): Date {
  const p = parseInternePeriode(periode)
  if (!p) return new Date(NaN)
  if (p.ritme === 'MAANDELIJKS') {
    return amsterdamAllDayStartToUtc(`${p.jaar}-${String(p.index).padStart(2, '0')}-01`)
  }
  if (p.ritme === 'KWARTAAL') {
    const maand = (p.index - 1) * 3 + 1
    return amsterdamAllDayStartToUtc(`${p.jaar}-${String(maand).padStart(2, '0')}-01`)
  }
  // 4-weken: maandag van ISO-week ((index-1)*4 + 1) in ISO-jaar `p.jaar`.
  const week = (p.index - 1) * 4 + 1
  const d = isoWeekMaandagUtcDatum(p.jaar, week)
  const iso = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
  return amsterdamAllDayStartToUtc(iso)
}

/** Kalenderdatum (als UTC-middernacht) van de maandag van ISO-week `week` in `isoJaar`. */
function isoWeekMaandagUtcDatum(isoJaar: number, week: number): Date {
  const vierJan = new Date(Date.UTC(isoJaar, 0, 4))
  const dow = (vierJan.getUTCDay() + 6) % 7 // ma = 0
  const maandagWeek1 = new Date(vierJan.getTime() - dow * DAG_MS)
  return new Date(maandagWeek1.getTime() + (week - 1) * 7 * DAG_MS)
}

/** Half-open UTC-bereik [start, eindExclusief) van een periode. */
export function internePeriodeRangeUtc(periode: string): { start: Date; eindExclusief: Date } {
  return {
    start: internePeriodeStartUtc(periode),
    eindExclusief: internePeriodeStartUtc(interneVolgendePeriode(periode)),
  }
}

/**
 * UTC-instant van 00:00 NL op dag `dag` (1-28) binnen de periode. Ligt de dag
 * voorbij het einde van de periode, dan valt hij terug op de laatste dag.
 *
 * Bewust via de KALENDER (dagen optellen op een UTC-datum, daarna door
 * `amsterdamAllDayStartToUtc`) en niet via `start + n * 24u`: valt de
 * zomer-/wintertijdwissel binnen de periode, dan zou dat de wandklok een uur
 * verschuiven en de factuurdatum op de vorige dag laten landen.
 */
export function internePeriodeDagUtc(periode: string, dag: number): Date {
  const { start, eindExclusief } = internePeriodeRangeUtc(periode)
  if (Number.isNaN(start.getTime())) return start
  const eersteDag = nlKalenderdag(start)
  const kalender = new Date(
    Date.UTC(eersteDag.jaar, eersteDag.maand - 1, eersteDag.dag + Math.max(1, dag) - 1),
  )
  const kandidaat = amsterdamAllDayStartToUtc(ymdVanUtcDatum(kalender))
  // Voorbij het einde van de periode? Terug naar de laatste kalenderdag.
  if (kandidaat.getTime() >= eindExclusief.getTime()) {
    const laatste = nlKalenderdag(new Date(eindExclusief.getTime() - 1))
    return amsterdamAllDayStartToUtc(
      `${laatste.jaar}-${String(laatste.maand).padStart(2, '0')}-${String(laatste.dag).padStart(2, '0')}`,
    )
  }
  return kandidaat
}

/** `YYYY-MM-DD` van een UTC-datum (kalenderdag, geen klok). */
function ymdVanUtcDatum(d: Date): string {
  return (
    `${d.getUTCFullYear()}-` +
    `${String(d.getUTCMonth() + 1).padStart(2, '0')}-` +
    `${String(d.getUTCDate()).padStart(2, '0')}`
  )
}

/** Alle periode-sleutels van `vanaf` t/m `totEnMet` (begrensd tegen een oneindige loop). */
export function internePeriodesTussen(vanaf: string, totEnMet: string, max = 60): string[] {
  const uit: string[] = []
  let huidig = vanaf
  const eind = internePeriodeStartUtc(totEnMet).getTime()
  if (Number.isNaN(eind)) return uit
  for (let i = 0; i < max; i++) {
    const start = internePeriodeStartUtc(huidig).getTime()
    if (Number.isNaN(start) || start > eind) break
    uit.push(huidig)
    if (huidig === totEnMet) break
    huidig = interneVolgendePeriode(huidig)
  }
  return uit
}

// ─── Omschrijvingssleutel ────────────────────────────────────────────────────

const PERIODE_WOORDEN = new Set([
  'januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus',
  'september', 'oktober', 'november', 'december',
  'january', 'february', 'march', 'may', 'june', 'july', 'august', 'october',
  'jan', 'feb', 'mrt', 'apr', 'jun', 'jul', 'aug', 'sep', 'sept', 'okt', 'oct', 'nov', 'dec',
  'kwartaal', 'maand', 'week', 'wk', 'periode', 'jaar', 'q',
])

/**
 * Herkenningssleutel van een omschrijving: kleine letters, accenten en leestekens
 * weg, en alle maand-/kwartaal-/jaar-/nummer-tokens eruit, zodat "Doorbelasting
 * personeel augustus 2026" en "Doorbelasting personeel september 2026" dezelfde
 * sleutel krijgen.
 */
export function interneOmschrijvingSleutel(omschrijving: string | null | undefined): string {
  if (!omschrijving) return ''
  return omschrijving
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(
      (w) =>
        w.length > 0 &&
        !/^\d+e?$/.test(w) &&
        !/^q[1-4]$/.test(w) &&
        !/^p\d{1,2}$/.test(w) &&
        !PERIODE_WOORDEN.has(w),
    )
    .join(' ')
    .trim()
}

// ─── Bedrag-vergelijking (hele centen) ───────────────────────────────────────

/** Mediaan van een niet-lege reeks centen (even lengte: gemiddelde van de twee middelste, naar beneden). */
export function medianCenten(centen: readonly number[]): number {
  if (centen.length === 0) return 0
  const s = [...centen].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  if (s.length % 2 === 1) return s[mid] as number
  return Math.floor(((s[mid - 1] as number) + (s[mid] as number)) / 2)
}

/** Absolute procentuele afwijking van `centen` t.o.v. `referentieCenten` (0 = gelijk). */
export function afwijkingPctCenten(centen: number, referentieCenten: number): number {
  if (referentieCenten === 0) return centen === 0 ? 0 : 100
  return Math.abs((centen - referentieCenten) / referentieCenten) * 100
}

/** Valt `centen` binnen `tolerantiePct` rond de referentie? */
export function bedragVergelijkbaar(
  centen: number,
  referentieCenten: number,
  tolerantiePct: number,
): boolean {
  return afwijkingPctCenten(centen, referentieCenten) <= tolerantiePct + 1e-9
}

// ─── Patroonherkenning ───────────────────────────────────────────────────────

/** Eén geboekte interne factuur die aan de analyse meedoet. */
export interface InternePatroonFactuur {
  id: string
  /** Factuurdatum (UTC-instant; de periode wordt in NL-tijd afgeleid). */
  factuurdatum: Date
  /** Bedrag exclusief btw, in hele centen. */
  bedragExclCenten: number
  /** Btw-percentage als getal (21), of null wanneer onbekend. */
  btwPercentage: number | null
  omschrijving: string | null
}

export interface InterneHerkendPatroon {
  ritme: InternRitme
  omschrijvingSleutel: string
  /** Representatieve omschrijving (de meest recente van de reeks). */
  omschrijving: string | null
  /** Mediaan-bedrag excl. btw in hele centen. */
  bedragExclCenten: number
  btwPercentage: number
  /** Dag in de periode (1-28) waarop het concept klaargezet wordt. */
  dagVanPeriode: number
  herkendAantal: number
  factuurIds: string[]
  /** Laatste periode waarin een factuur van dit patroon geboekt is. */
  laatstePeriode: string
}

export interface InternePatroonDrempels {
  minHerkenningen: number
  bedragTolerantiePct: number
  ritmeTolerantieDagen: number
}

/**
 * Herken terugkerende reeksen binnen de facturen van ÉÉN BV-paar.
 *
 * Werkwijze (conservatief, spec §6 + aanname A2):
 *   1. groepeer op omschrijvingssleutel (lege sleutel = eigen groep "zonder
 *      omschrijving", die ook een patroon mag vormen);
 *   2. bepaal per groep het ritme uit de mediaan van de dag-intervallen tussen
 *      opeenvolgende facturen (met `ritmeTolerantieDagen` speling);
 *   3. zet de facturen op periode-sleutel; meerdere facturen in dezelfde periode
 *      tellen als één (de laatste wint) - anders zou een correctiefactuur het
 *      ritme breken;
 *   4. neem de LANGSTE aaneengesloten staart van periodes waarvan het bedrag
 *      binnen de tolerantie rond de mediaan valt;
 *   5. >= `minHerkenningen` periodes -> kandidaat.
 */
export function herkenInternePatronen(
  facturen: readonly InternePatroonFactuur[],
  drempels: InternePatroonDrempels,
): InterneHerkendPatroon[] {
  const perSleutel = new Map<string, InternePatroonFactuur[]>()
  for (const f of facturen) {
    const sleutel = interneOmschrijvingSleutel(f.omschrijving)
    const lijst = perSleutel.get(sleutel) ?? []
    lijst.push(f)
    perSleutel.set(sleutel, lijst)
  }

  const uit: InterneHerkendPatroon[] = []
  for (const [sleutel, groep] of perSleutel) {
    const kandidaat = herkenEenReeks(sleutel, groep, drempels)
    if (kandidaat) uit.push(kandidaat)
  }
  // Meeste herkenningen eerst, dan hoogste bedrag: het sterkste patroon bovenaan.
  return uit.sort(
    (a, b) => b.herkendAantal - a.herkendAantal || b.bedragExclCenten - a.bedragExclCenten,
  )
}

function herkenEenReeks(
  sleutel: string,
  groep: readonly InternePatroonFactuur[],
  drempels: InternePatroonDrempels,
): InterneHerkendPatroon | null {
  if (groep.length < drempels.minHerkenningen) return null
  const oplopend = [...groep].sort((a, b) => a.factuurdatum.getTime() - b.factuurdatum.getTime())

  // Elk ritme als hypothese proberen i.p.v. het ritme uit de rauwe intervallen
  // af te leiden: een correctiefactuur in dezelfde periode zou dat interval
  // anders breken. Per ritme wint de LANGSTE passende reeks; bij gelijke lengte
  // de voorkeursvolgorde hieronder (maand is verreweg het gangbaarst voor
  // doorbelastingen, en een maandreeks kan toevallig ook op 4-wekenblokken
  // passen).
  const VOORKEUR: readonly InternRitme[] = ['MAANDELIJKS', 'KWARTAAL', 'VIER_WEKEN']
  let beste: InterneHerkendPatroon | null = null
  for (const ritme of VOORKEUR) {
    const kandidaat = herkenVoorRitme(sleutel, oplopend, ritme, drempels)
    if (!kandidaat) continue
    if (!beste || kandidaat.herkendAantal > beste.herkendAantal) beste = kandidaat
  }
  return beste
}

/** Eén ritme-hypothese toetsen op de (oplopend gesorteerde) facturen van één sleutel. */
function herkenVoorRitme(
  sleutel: string,
  oplopend: readonly InternePatroonFactuur[],
  ritme: InternRitme,
  drempels: InternePatroonDrempels,
): InterneHerkendPatroon | null {
  // Eén factuur per periode; de EERSTE wint. Een later in dezelfde periode
  // binnengekomen correctie verzet zo niet de klaarzetdag of het ritme.
  const perPeriode = new Map<string, InternePatroonFactuur>()
  for (const f of oplopend) {
    const sleutelPeriode = internePeriodeVanDatum(f.factuurdatum, ritme)
    if (!perPeriode.has(sleutelPeriode)) perPeriode.set(sleutelPeriode, f)
  }
  const periodes = [...perPeriode.keys()].sort(
    (a, b) => internePeriodeStartUtc(a).getTime() - internePeriodeStartUtc(b).getTime(),
  )
  if (periodes.length < drempels.minHerkenningen) return null

  // Langste aaneengesloten staart: elke periode volgt exact op de vorige.
  let vanaf = periodes.length - 1
  while (vanaf > 0) {
    const vorige = periodes[vanaf - 1] as string
    if (interneVolgendePeriode(vorige) !== (periodes[vanaf] as string)) break
    vanaf -= 1
  }
  const staart = periodes.slice(vanaf)
  if (staart.length < drempels.minHerkenningen) return null

  const rijen = staart.map((p) => perPeriode.get(p) as InternePatroonFactuur)

  // Tweede toets: liggen de werkelijke factuurdatums ook op dit ritme? Zonder
  // deze check zou een maandreeks óók als 4-wekenreeks kunnen passen zodra de
  // blokken toevallig opschuiven.
  const intervallen: number[] = []
  for (let i = 1; i < rijen.length; i++) {
    const vorige = rijen[i - 1] as InternePatroonFactuur
    const huidige = rijen[i] as InternePatroonFactuur
    intervallen.push(
      Math.round((huidige.factuurdatum.getTime() - vorige.factuurdatum.getTime()) / DAG_MS),
    )
  }
  if (
    intervallen.length > 0 &&
    Math.abs(medianCenten(intervallen) - INTERN_RITME_DAGEN[ritme]) > drempels.ritmeTolerantieDagen
  ) {
    return null
  }

  // Bedrag: krimp de staart van voren zolang een bedrag buiten de tolerantie
  // rond de mediaan valt (het patroon werd dan pas later stabiel).
  const mediaan = medianCenten(rijen.map((f) => f.bedragExclCenten))
  let start = 0
  while (
    start < rijen.length &&
    !bedragVergelijkbaar(
      (rijen[start] as InternePatroonFactuur).bedragExclCenten,
      mediaan,
      drempels.bedragTolerantiePct,
    )
  ) {
    start += 1
  }
  const passend = rijen.slice(start)
  const passendePeriodes = staart.slice(start)
  if (passend.length < drempels.minHerkenningen) return null

  // Een uitschieter MIDDEN in de reeks breekt het patroon: dan is het bedrag
  // niet "vergelijkbaar" maar wisselend (spec §6.4, geen voorstel).
  const definitieveMediaan = medianCenten(passend.map((f) => f.bedragExclCenten))
  if (
    passend.some(
      (f) =>
        !bedragVergelijkbaar(f.bedragExclCenten, definitieveMediaan, drempels.bedragTolerantiePct),
    )
  ) {
    return null
  }

  const laatste = passend[passend.length - 1] as InternePatroonFactuur
  const eerste = passend[0] as InternePatroonFactuur
  return {
    ritme,
    omschrijvingSleutel: sleutel,
    omschrijving: laatste.omschrijving,
    bedragExclCenten: definitieveMediaan,
    btwPercentage: laatste.btwPercentage ?? 21,
    // Klaarzetdag uit de EERSTE factuur van de passende reeks: die is niet door
    // een latere correctie verschoven.
    dagVanPeriode: Math.min(28, Math.max(1, nlKalenderdag(eerste.factuurdatum).dag)),
    herkendAantal: passend.length,
    factuurIds: passend.map((f) => f.id),
    laatstePeriode: passendePeriodes[passendePeriodes.length - 1] as string,
  }
}
